import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Expand, Share2, X, ZoomIn, ZoomOut } from "lucide-react";
import { ARTIST, assetUrl, siteImg } from "@/lib/constants";
import { Bindi } from "@/components/motifs";
import { artworkAlt } from "@/lib/site";

export const ArtworkViewer = ({ artworks, index, onClose, onIndexChange }) => {
  const art = artworks[index];
  const [zoomed, setZoomed] = useState(false);
  const containerRef = useRef(null);

  const prev = useCallback(
    () => onIndexChange((index - 1 + artworks.length) % artworks.length),
    [index, artworks.length, onIndexChange]
  );
  const next = useCallback(
    () => onIndexChange((index + 1) % artworks.length),
    [index, artworks.length, onIndexChange]
  );

  useEffect(() => {
    setZoomed(false);
  }, [index]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, prev, next]);

  const share = async () => {
    const url = `${window.location.origin}/artwork/${art.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Artwork link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const fullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-[90] flex flex-col bg-[#0C0B0A] text-[#F5F2EC]"
      data-testid="artwork-modal-viewer"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4 sm:px-8">
        <div className="flex items-center gap-3">
          <img src={siteImg("/assets/logo.png")} alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/25" />
          <div className="leading-tight">
            <p className="font-serif text-sm font-medium">{ARTIST.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">The Gallery</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={share} data-testid="artwork-modal-share-button" aria-label="Share artwork" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C]">
            <Share2 size={15} />
          </button>
          <button onClick={fullscreen} data-testid="artwork-modal-fullscreen-button" aria-label="Toggle fullscreen" className="hidden h-10 w-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C] sm:flex">
            <Expand size={15} />
          </button>
          <button
            onClick={() => setZoomed((z) => !z)}
            data-testid="artwork-modal-zoom-button"
            aria-label={zoomed ? "Zoom out" : "Zoom in"}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C]"
          >
            {zoomed ? <ZoomOut size={15} /> : <ZoomIn size={15} />}
          </button>
          <button onClick={onClose} data-testid="artwork-modal-close-button" aria-label="Close viewer" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C]">
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 lg:grid-cols-[1fr_400px]">
        <div className="relative flex min-h-[42vh] items-center justify-center overflow-hidden p-6 sm:p-10 lg:min-h-0 lg:p-14">
          <motion.div
            key={art.id}
            initial={{ opacity: 0, scale: 0.985 }}
            animate={{ opacity: 1, scale: zoomed ? 1.8 : 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            drag={zoomed}
            dragConstraints={{ left: -500, right: 500, top: -400, bottom: 400 }}
            className={`relative mx-auto w-fit ${zoomed ? "cursor-grab active:cursor-grabbing" : ""}`}
          >
            <div className="pointer-events-none absolute -inset-3 border border-white/30" />
            <div className="pointer-events-none absolute -inset-6 border border-white/10" />
            <img
              src={assetUrl(art.image)}
              alt={artworkAlt(art)}
              draggable="false"
              className="block max-h-[60vh] w-auto max-w-full object-contain lg:max-h-[78vh]"
            />
          </motion.div>

          <div className="pointer-events-none absolute bottom-5 left-5 hidden items-center gap-3 lg:flex">
            <span className="relative">
              <svg viewBox="0 0 60 60" className="animate-spin-slow absolute -inset-1.5 h-[calc(100%+0.75rem)] w-[calc(100%+0.75rem)] text-white/30">
                <circle cx="30" cy="30" r="28" fill="none" stroke="currentColor" strokeWidth="0.8" strokeDasharray="1.5 6" />
              </svg>
              <img src={siteImg("/assets/logo.png")} alt={`${ARTIST.name} seal`} className="h-14 w-14 rounded-full object-cover opacity-90 ring-1 ring-white/30" />
            </span>
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
              N° {String(art.order || index + 1).padStart(2, "0")} · Mithila
            </p>
          </div>

          {artworks.length > 1 && (
            <>
              <button onClick={prev} data-testid="artwork-modal-prev-button" aria-label="Previous artwork" className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/30 backdrop-blur transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C] lg:left-6">
                <ChevronLeft size={18} />
              </button>
              <button onClick={next} data-testid="artwork-modal-next-button" aria-label="Next artwork" className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-black/30 backdrop-blur transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C] lg:right-6">
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </div>

        <aside className="min-h-0 overflow-y-auto border-t border-white/10 p-6 sm:p-8 lg:border-l lg:border-t-0" data-testid="artwork-modal-details">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E74C3C]">
            {(art.themes || []).join(" · ") || "Mithila / Madhubani"}
          </p>
          <h3 className="mt-3 font-serif text-3xl font-medium leading-tight tracking-tight">{art.title}</h3>
          {art.intro && <p className="mt-4 font-serif text-lg italic leading-snug text-white/70">{art.intro}</p>}

          <div className="mt-6 space-y-4">
            {(art.story || []).map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-white/65">{p}</p>
            ))}
          </div>

          {(art.visual_language || []).length > 0 && (
            <div className="mt-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">Visual language</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {art.visual_language.map((v) => (
                  <span key={v} className="border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/60">{v}</span>
                ))}
              </div>
            </div>
          )}

          {(art.motifs || []).length > 0 && (
            <div className="mt-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">Mithila elements</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {art.motifs.map((m) => (
                  <span key={m} className="flex items-center gap-2 border border-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-white/60">
                    <Bindi size={5} /> {m}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(art.medium || art.year || art.dimensions) && (
            <dl className="mt-8 space-y-2 border-t border-white/10 pt-6">
              {art.medium && <div className="flex justify-between text-xs"><dt className="text-white/40">Medium</dt><dd>{art.medium}</dd></div>}
              {art.year && <div className="flex justify-between text-xs"><dt className="text-white/40">Year</dt><dd>{art.year}</dd></div>}
              {art.dimensions && <div className="flex justify-between text-xs"><dt className="text-white/40">Dimensions</dt><dd>{art.dimensions}</dd></div>}
            </dl>
          )}

          <Link
            to={`/artwork/${art.id}`}
            data-testid="artwork-modal-catalogue-link"
            className="mt-10 inline-flex items-center gap-2 border-b border-[#E74C3C] pb-1 font-mono text-[11px] uppercase tracking-[0.22em] text-[#E74C3C] transition-opacity hover:opacity-70"
          >
            Open catalogue page
          </Link>
        </aside>
      </div>
    </motion.div>
  );
};
